// Pure number-formatting helpers for axis ticks and other space-constrained
// numeric labels. No React, no DOM.

// Magnitude unit (divisor + suffix) for a non-negative magnitude. Convention:
// lowercase "k" (the house style / finance idiom), uppercase M/B/T. Shared by the
// single-value and tick-set formatters so they pick units the same way.
function unitFor(abs) {
  if (abs >= 1e12) return { div: 1e12, suffix: "T" };
  if (abs >= 1e9) return { div: 1e9, suffix: "B" };
  if (abs >= 1e6) return { div: 1e6, suffix: "M" };
  if (abs >= 1e3) return { div: 1e3, suffix: "k" };
  return { div: 1, suffix: "" };
}

// Trim a trailing ".0"/".500"→".5" etc.: drop trailing zeros after a decimal
// point, and a bare trailing point. "6.60" → "6.6", "56.0" → "56", "56" → "56".
function trimZeros(s) {
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}

/**
 * Format a number compactly with a magnitude suffix (k / M / B / T), so a label
 * stays narrow no matter how large the value — e.g. 597500 → "597.5k",
 * 54_300_000 → "54.3M", 59_270_616_000 → "59.3B". This exists because a naive
 * "always divide by 1000 + k" produced absurd 8–9 char labels ("59270616k") on
 * large-revenue signals, which overran the chart's Y-axis gutter and collided
 * with the rotated axis title.
 *
 * One decimal place, with a trailing ".0" trimmed ("1000" → "1k", not "1.0k").
 * Values below 1000 render as-is (integers untouched; fractionals get one
 * decimal, trailing ".0" trimmed). The sign is preserved for negatives.
 *
 * Non-finite input (NaN / ±Infinity) is returned via String() rather than
 * producing a bogus "NaNk" — a defensive passthrough matching the other
 * best-effort formatters in this codebase.
 *
 * @param {number} v the value to format
 * @returns {string} the compact label
 */
export function formatCompactNumber(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return String(v);
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  const { div, suffix } = unitFor(abs);
  if (div === 1) {
    // Below 1000: integers stay exact; fractionals get one decimal (".0" trimmed).
    return sign + (Number.isInteger(abs) ? String(abs) : trimZeros(abs.toFixed(1)));
  }
  return sign + trimZeros((abs / div).toFixed(1)) + suffix;
}

/**
 * Format a SET of axis tick values with a SHARED magnitude unit and just enough
 * decimal places that every tick renders distinctly. Fixed 1-decimal formatting
 * collapses a tight range to repeated labels — world-population ticks spanning
 * 6.60B..6.70B would all read "6.6B"/"6.7B", hiding real change. This picks the
 * fewest decimals (starting at 1, so the common case looks identical to
 * formatCompactNumber) that keep all ticks unique, up to `maxDecimals`.
 *
 * The unit is chosen once from the largest-magnitude tick so the whole axis reads
 * in one unit (e.g. all "B"). Trailing zeros are trimmed per label (trimming can
 * never merge two already-distinct rounded values, so distinctness is preserved),
 * and exact zero renders as "0" (no unit). Non-finite entries pass through via
 * String(). Sign is preserved for negatives.
 *
 * @param {number[]} values the tick values, in axis order
 * @param {number} [maxDecimals=3] cap on decimal places (a degenerate near-equal
 *   range may still repeat at the cap; real axis ranges resolve well below it)
 * @returns {string[]} formatted labels, 1:1 with `values`
 */
export function formatCompactTicks(values, maxDecimals = 3) {
  const arr = Array.isArray(values) ? values : [];
  const finite = arr.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (!finite.length) return arr.map((v) => String(v));
  // One shared unit from the largest magnitude, so every tick reads in it.
  const { div, suffix } = unitFor(Math.max(...finite.map((v) => Math.abs(v))));
  // Fewest decimals in [1, maxDecimals] that render every finite tick distinctly
  // — tried inclusive of maxDecimals, and if none fully separate the ticks we keep
  // maxDecimals (the closest we're allowed). Distinctness is checked on the
  // untrimmed toFixed(d); trimming zeros afterwards can't merge two values that
  // were already distinct at d decimals.
  let decimals = 1;
  for (let d = 1; d <= maxDecimals; d++) {
    decimals = d;
    const seen = new Set(finite.map((v) => (v / div).toFixed(d)));
    if (seen.size === finite.length) break;
  }
  return arr.map((v) => {
    if (typeof v !== "number" || !Number.isFinite(v)) return String(v);
    if (v === 0) return "0";
    return trimZeros((v / div).toFixed(decimals)) + suffix;
  });
}
